/* Progressive enhancement: links, native forms and FAQ work without JavaScript. */
(() => {
  if (customElements.get('spc-customize')) return;
  class StrapCustomize extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.cards = Array.from(this.querySelectorAll('[data-spc-case]'));
      this.stack = this.querySelector('[data-spc-cases]');
      this.caseScroll = this.querySelector('[data-spc-scroll]');
      this.caseStage = this.querySelector('[data-spc-stage]');
      this.activeIndex = 0;
      this.manualSelection = false;
      this.querySelectorAll('.spc-camera-strip').forEach(strip => {
        const group = strip.querySelector('.spc-camera-group');
        if (!strip.querySelector('[data-spc-camera-copy]')) {
          const copy = group.cloneNode(true);
          copy.setAttribute('aria-hidden', 'true');
          copy.setAttribute('data-spc-camera-copy', '');
          copy.querySelectorAll('[data-camera]').forEach(camera => camera.removeAttribute('data-camera'));
          strip.append(copy);
        }
      });
      const cameras = this.querySelector('.spc-cameras');
      const pauseCameras = this.querySelector('[data-spc-pause-cameras]');
      cameras.classList.add('is-moving');
      pauseCameras.hidden = false;
      pauseCameras.addEventListener('click', () => {
        const paused = cameras.classList.toggle('is-paused');
        pauseCameras.setAttribute('aria-label', paused ? 'Resume camera animation' : 'Pause camera animation');
        pauseCameras.setAttribute('aria-pressed', String(paused));
      }, options);
      this.querySelectorAll('[data-spc-intent]').forEach(link => link.addEventListener('click', () => {
        const isTemplate = link.dataset.spcIntent === 'template';
        const form = this.querySelector('.spc-contact-form');
        form.querySelector('[data-spc-request-type]').value = isTemplate ? 'Illustrator template request' : 'Full bleed artwork';
        const message = form.querySelector('textarea');
        if (!message.value.trim() || message.dataset.prefilled === message.value) {
          message.value = isTemplate ? "I'd like the Illustrator template for a custom camera wrap." : "I'd like to send full bleed artwork for my custom cameras.";
          message.dataset.prefilled = message.value;
        }
        // Keep native hash navigation and move keyboard focus into the destination.
        requestAnimationFrame(() => form.querySelector('input[type="text"]').focus({ preventScroll: true }));
      }, options));
      this.querySelectorAll('.spc-faq details').forEach(detail => detail.addEventListener('toggle', () => {
        if (detail.open) this.querySelectorAll('.spc-faq details').forEach(other => { if (other !== detail) other.open = false; });
      }, options));
      this.dialog = this.querySelector('[data-spc-dialog]');
      this.player = this.querySelector('[data-spc-player]');
      this.videoFrame = this.querySelector('[data-spc-video-frame]');
      this.loadingMessage = this.querySelector('[data-spc-loading]');
      this.retryVideoButton = this.querySelector('[data-spc-retry]');
      this.retryVideoButton.addEventListener('click', () => this.startVideo(true), options);
      this.querySelectorAll('[data-spc-play]').forEach(button => button.addEventListener('click', () => this.openVideo(button), options));
      this.querySelector('[data-spc-close]').addEventListener('click', () => this.dialog.close(), options);
      this.dialog.addEventListener('close', () => {
        this.clearVideo();
        this.lastVideoButton?.focus({ preventScroll: true });
      }, options);
      this.dialog.addEventListener('click', event => { if (event.target === this.dialog) { const r = this.dialog.getBoundingClientRect(); if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) this.dialog.close(); } }, options);
      if (this.cards.length > 1) {
        this.stack.addEventListener('keydown', event => {
          if (!this.scrollEnabled || !['ArrowLeft','ArrowRight','Home','End'].includes(event.key)) return;
          event.preventDefault();
          const next = event.key === 'Home' ? 0 : event.key === 'End' ? this.cards.length - 1 : this.activeIndex + (event.key === 'ArrowRight' ? 1 : -1);
          this.selectManually(next);
        }, options);
        this.stack.setAttribute('role', 'region');
        this.stack.addEventListener('touchstart', event => {
          const touch = event.touches[0];
          this.touchOrigin = touch ? { x: touch.clientX, y: touch.clientY } : null;
        }, { ...options, passive: true });
        this.stack.addEventListener('touchend', event => {
          const touch = event.changedTouches[0];
          if (!this.scrollEnabled || !this.touchOrigin || !touch) return;
          const dx = touch.clientX - this.touchOrigin.x;
          const dy = touch.clientY - this.touchOrigin.y;
          this.touchOrigin = null;
          if (Math.abs(dx) > 48 && Math.abs(dx) > Math.abs(dy) * 1.4) this.selectManually(this.activeIndex + (dx < 0 ? 1 : -1));
        }, { ...options, passive: true });
        const onScroll = () => {
          if (this.raf) return;
          this.raf = requestAnimationFrame(() => { this.raf = null; this.onScroll(); });
        };
        window.addEventListener('scroll', onScroll, { ...options, passive: true });
        // Restoring focus after a modal must not lock out the next wheel/touch scroll.
        // Keep keyboard focus until the visitor explicitly switches back to scrolling.
        window.addEventListener('wheel', event => {
          if (Math.abs(event.deltaY) > Math.abs(event.deltaX)) this.resumeCaseScroll();
        }, { ...options, passive: true });
        window.addEventListener('touchstart', event => {
          const touch = event.touches[0];
          this.scrollTouchOrigin = touch ? { x: touch.clientX, y: touch.clientY } : null;
        }, { ...options, passive: true });
        window.addEventListener('touchmove', event => {
          const touch = event.touches[0];
          if (!touch || !this.scrollTouchOrigin) return;
          const dx = touch.clientX - this.scrollTouchOrigin.x;
          const dy = touch.clientY - this.scrollTouchOrigin.y;
          if (Math.abs(dy) > 8 && Math.abs(dy) > Math.abs(dx)) this.resumeCaseScroll();
        }, { ...options, passive: true });
        window.addEventListener('keydown', event => {
          if (event.target.closest('input,textarea,select,[contenteditable="true"]')) return;
          if (['PageDown','PageUp','ArrowDown','ArrowUp'].includes(event.key) || (event.key === ' ' && !event.target.closest('button,a,summary'))) this.resumeCaseScroll();
        }, options);
        const onResize = () => {
          if (this.layoutRaf) cancelAnimationFrame(this.layoutRaf);
          this.layoutRaf = requestAnimationFrame(() => { this.layoutRaf = null; this.syncCaseLayout(); });
        };
        window.addEventListener('resize', onResize, { ...options, passive: true });
        this.motion.addEventListener('change', onResize, options);
        this.syncCaseLayout();
        document.fonts?.ready.then(() => { if (this.isConnected && this.controller) onResize(); });
      }
      // Keep the draft context on the customizer route during preview.
      const previewId = new URL(window.location.href).searchParams.get('preview_theme_id');
      if (previewId) this.querySelectorAll('[data-spc-customizer]').forEach(link => {
        const destination = new URL(link.href, window.location.href);
        if (destination.origin === window.location.origin) { destination.searchParams.set('preview_theme_id', previewId); link.href = destination.href; }
      });
      this.addEventListener('shopify:block:select', event => {
        const card = event.target.closest('[data-spc-case]');
        if (card) this.selectManually(this.cards.indexOf(card));
        const detail = event.target.closest('details');
        if (detail) detail.open = true;
      }, options);
      const formResult = this.querySelector('[data-spc-form-result]');
      if (formResult) { formResult.scrollIntoView({ block: 'center' }); formResult.focus({ preventScroll: true }); }
    }
    setCase(index) {
      this.renderCaseProgress((index + this.cards.length) % this.cards.length);
    }
    resumeCaseScroll() {
      if (this.dialog.open || !this.scrollEnabled) return;
      this.manualSelection = false;
      if (this.stack.contains(document.activeElement)) document.activeElement.blur();
    }
    selectManually(index) {
      if (!this.scrollEnabled) return;
      this.manualSelection = true;
      this.stack.classList.add('is-manual');
      if (this.stack.contains(document.activeElement)) this.stack.focus({ preventScroll: true });
      this.setCase(index);
    }
    syncCaseLayout() {
      const previousIndex = this.activeIndex;
      this.stack.style.removeProperty('width');
      this.stack.style.removeProperty('height');
      this.stack.classList.add('is-ready');
      let stackHeight = Math.max(...this.cards.map(card => card.offsetHeight)) + 60;
      const available = window.innerHeight - 64;
      if (stackHeight > available && !this.motion.matches) {
        const fittedWidth = Math.max(280, this.stack.getBoundingClientRect().width - (stackHeight - available) * 16 / 9);
        this.stack.style.width = `${fittedWidth}px`;
        stackHeight = Math.max(...this.cards.map(card => card.offsetHeight)) + 60;
      }
      // Show all cases in normal flow when animation is unwanted or the viewport cannot fit a card.
      this.scrollEnabled = !this.motion.matches && stackHeight <= available;
      this.caseScroll.classList.toggle('is-scrollytelling', this.scrollEnabled);
      this.stack.classList.toggle('is-ready', this.scrollEnabled);
      this.stack.classList.remove('is-manual');
      if (!this.scrollEnabled) {
        this.stack.style.removeProperty('width');
        this.stack.removeAttribute('tabindex');
        this.stack.setAttribute('aria-label', 'Case studies');
        this.cards.forEach(card => { card.removeAttribute('style'); card.inert = false; card.removeAttribute('aria-hidden'); });
        return;
      }
      this.stack.style.height = `${stackHeight}px`;
      this.stackHeight = stackHeight;
      this.stickyTop = Math.max(24, (window.innerHeight - stackHeight) / 2);
      this.scrollTravel = Math.max(720, window.innerHeight * .95) * (this.cards.length - 1);
      this.caseScroll.style.setProperty('--spc-case-height', `${stackHeight}px`);
      this.caseScroll.style.setProperty('--spc-case-travel', `${this.scrollTravel}px`);
      this.caseScroll.style.setProperty('--spc-case-top', `${this.stickyTop}px`);
      this.stack.tabIndex = 0;
      this.stack.setAttribute('aria-label', 'Case studies. Scroll to explore, swipe, or use the left and right arrow keys.');
      this.setCase(previousIndex);
      this.onScroll();
    }
    renderCaseProgress(progress) {
      this.caseProgress = progress;
      const active = Math.min(this.cards.length - 1, Math.round(progress));
      this.cards.forEach((card, i) => {
        const distance = i - progress;
        let x = 0, y = 1, rotation = 0, opacity = 1, shade = 0;
        if (distance < 0) {
          const departure = Math.min(1, -distance);
          x = -40 * departure;
          y = 1 - (this.stackHeight + this.stickyTop + 120) * departure;
          rotation = -7 * departure;
          // Keep the departing photograph opaque until it has cleared the next card.
          opacity = 1 - Math.max(0, (departure - .82) / .18);
        } else {
          const depth = Math.min(2, distance);
          y = depth < 1 ? 1 + 19 * depth : 20 + 20 * (depth - 1);
          rotation = depth < 1 ? 2 * depth : 2 - 4 * (depth - 1);
          shade = depth * .4;
        }
        card.style.setProperty('--spc-card-x', `${x.toFixed(3)}px`);
        card.style.setProperty('--spc-card-y', `${y.toFixed(3)}px`);
        card.style.setProperty('--spc-card-rotation', `${rotation.toFixed(3)}deg`);
        card.style.setProperty('--spc-card-opacity', opacity.toFixed(3));
        card.style.setProperty('--spc-card-shade', shade.toFixed(3));
        card.style.zIndex = String(this.cards.length - i);
        card.dataset.depth = String(i - active);
        card.inert = i !== active;
        card.setAttribute('aria-hidden', String(i !== active));
      });
      this.activeIndex = active;
      const status = this.querySelector('[data-spc-case-status]');
      const description = `${active + 1} / ${this.cards.length}: ${this.cards[active].dataset.title}`;
      if (status.textContent !== description) status.textContent = description;
    }
    onScroll() {
      if (!this.scrollEnabled || this.dialog.open) return;
      const rect = this.caseScroll.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > window.innerHeight) this.manualSelection = false;
      if (this.manualSelection || this.stack.contains(document.activeElement)) return;
      this.stack.classList.remove('is-manual');
      const progress = Math.max(0, Math.min(this.cards.length - 1, (this.stickyTop - rect.top) / this.scrollTravel * (this.cards.length - 1)));
      const segment = Math.floor(progress);
      const release = Math.max(0, Math.min(1, (progress - segment - .25) / .65));
      const eased = release * release * (3 - 2 * release);
      this.renderCaseProgress(segment + eased);
    }
    openVideo(button) {
      const card = button.closest('[data-spc-case]');
      const template = card.querySelector('[data-spc-video]');
      if (!template) return;
      this.clearVideo();
      this.lastVideoButton = button;
      this.querySelector('#spc-dialog-title').textContent = card.dataset.title;
      this.querySelector('[data-spc-media-error]').hidden = true;
      this.player.replaceChildren(template.content.cloneNode(true));
      const ratio = Number(template.dataset.aspectRatio);
      this.videoFrame.style.setProperty('--spc-video-ratio', Number.isFinite(ratio) && ratio > 0 ? ratio : 16 / 9);
      this.videoController = new AbortController();
      const options = { signal: this.videoController.signal };
      this.dialog.showModal();
      const video = this.player.querySelector('video');
      if (video) {
        this.currentVideo = video;
        const loading = () => { this.loadingMessage.hidden = false; this.player.setAttribute('aria-busy', 'true'); };
        const ready = () => { this.loadingMessage.hidden = true; this.player.removeAttribute('aria-busy'); };
        video.addEventListener('waiting', loading, options);
        video.addEventListener('playing', () => { ready(); this.retryVideoButton.hidden = true; }, options);
        video.addEventListener('pause', ready, options);
        video.addEventListener('ended', ready, options);
        const failed = () => {
          this.videoLoadFailed = true;
          ready();
          this.querySelector('[data-spc-media-error]').hidden = false;
          this.retryVideoButton.textContent = 'Retry video';
          this.retryVideoButton.hidden = false;
        };
        video.addEventListener('error', failed, options);
        const sources = [...video.querySelectorAll('source')];
        this.failedVideoSources = new Set();
        sources.forEach(source => source.addEventListener('error', () => {
          this.failedVideoSources.add(source);
          if (this.failedVideoSources.size === sources.length) failed();
        }, options));
        // Explicitly select the cloned sources before playing in the same user gesture.
        // The frame has its final dimensions even while Safari is still loading metadata.
        video.preload = 'auto';
        video.load();
        this.startVideo();
      }
    }
    startVideo(retry = false) {
      const video = this.currentVideo;
      if (!video || !this.dialog.open) return;
      const session = this.videoController;
      const attemptId = this.videoAttempt = (this.videoAttempt || 0) + 1;
      this.querySelector('[data-spc-media-error]').hidden = true;
      this.retryVideoButton.hidden = true;
      this.loadingMessage.hidden = false;
      this.player.setAttribute('aria-busy', 'true');
      if (retry && (video.error || this.videoLoadFailed)) {
        this.videoLoadFailed = false;
        this.failedVideoSources.clear();
        video.load();
      }
      const attempt = video.play();
      attempt?.catch(() => {
        // A closed/replaced player's pending promise must never change the new dialog.
        if (session.signal.aborted || this.videoAttempt !== attemptId || this.currentVideo !== video || !this.dialog.open) return;
        this.loadingMessage.hidden = true;
        this.player.removeAttribute('aria-busy');
        this.retryVideoButton.textContent = video.error || this.videoLoadFailed ? 'Retry video' : 'Play video';
        this.retryVideoButton.hidden = false;
      });
    }
    clearVideo() {
      this.videoController?.abort();
      const video = this.currentVideo;
      this.currentVideo = null;
      this.videoLoadFailed = false;
      if (video) {
        video.pause();
        video.removeAttribute('src');
        video.querySelectorAll('source').forEach(source => source.removeAttribute('src'));
        video.load();
      }
      this.player?.replaceChildren();
      this.player?.removeAttribute('aria-busy');
      if (this.loadingMessage) this.loadingMessage.hidden = true;
      if (this.retryVideoButton) this.retryVideoButton.hidden = true;
    }
    disconnectedCallback() { this.controller?.abort(); this.controller = null; if (this.raf) cancelAnimationFrame(this.raf); if (this.layoutRaf) cancelAnimationFrame(this.layoutRaf); this.clearVideo(); }
  }
  customElements.define('spc-customize', StrapCustomize);
})();
